import { getRuntimeValue } from "@3sc/common-component";
import { ThemeProvider } from "@mui/material";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import {
  createContext,
  default as React,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelectedMenu } from "../../hooks/useSelectedMenu";
import useCustomEntitySync from "../../hooks/useCustomEntitySync";
import { updateSettings } from "../../utils/ApiHandler";
import { defaultTheme } from "../../utils/const";
import { ENUM_EVENT, sendDataToChild } from "../../utils/eventEmitter";
import { customTheme } from "../../utils/theme";
import SideNav from "../NavigationMenu/SideNav";
import TopNav from "../NavigationMenu/TopNav";
import "./styles.scss";
import SideDrawer from "../SideDrawer";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../provider/AuthContext";

const ThemeContext = createContext();
export const useThemeToggle = () => useContext(ThemeContext);

const SCAILayout = ({ children }) => {
  const { t } = useTranslation();
  useCustomEntitySync();
  const { settings, user, selectedBusinessUnit, setSettings } = useAuth();
  const theme = settings?.platform?.theme || defaultTheme;
  const podTheme = customTheme[theme];
  const [mode, setMode] = useState(theme);
  const [open, setOpen] = React.useState(false);
  const [sliderOpen, setSliderOpen] = React.useState(false);
  const [isMerged, setIsMerged] = React.useState(false);
  const [drawerExpanded, setDrawerExpanded] = React.useState(false);
  const drawerSlotRef = useRef(null);
  const drawerScriptLoaded = useRef(false);
  const drawerMounted = useRef(false);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleAiChatClick = () => {
    setSliderOpen(!sliderOpen);
  };

  useEffect(() => {
    sendDataToChild({ eventType: ENUM_EVENT.LOGOUT, theme: { logout: true } });
  }, [window.location.pathname]);

  useEffect(() => {
    if (!sliderOpen) return;
    if (drawerMounted.current) return;

    let slotPoll = null;
    let scriptPoll = null;

    const mount = () => {
      const el = drawerSlotRef.current;
      if (!window.renderDrawerComponent) return false;

      window.renderDrawerComponent(el, {
        open: sliderOpen,
        expanded: drawerExpanded,
        compact: !drawerExpanded,
      });
      drawerMounted.current = true;
      return true;
    };

    const loadScript = () => {
      const host = import.meta.env.VITE_GENAI_HOST;
      const name = "gen-ai";
      const scriptId = `micro-frontend-script-${name}`;

      if (document.getElementById(scriptId)) {
        scriptPoll = setInterval(() => {
          if (mount()) {
            clearInterval(scriptPoll);
            drawerScriptLoaded.current = true;
          }
        }, 50);
        return;
      }

      fetch(`${host}/${name}/asset-manifest.json`)
        .then((res) => res.json())
        .then((manifest) => {
          let jsFile,
            cssFile,
            isVite = false;
          if (manifest["files"]) {
            jsFile = manifest["files"]["main.js"];
            cssFile = manifest["files"]["main.css"];
          } else {
            isVite = true;
            const entry = Object.values(manifest).find((file) => file.isEntry);
            jsFile = entry.file;
            cssFile = entry.css ? entry.css[0] : null;
          }

          const script = document.createElement("script");
          script.id = scriptId;
          script.crossOrigin = "cors";
          script.src = `${host}${jsFile}`;
          if (isVite) script.type = "module";
          script.onload = () => {
            drawerScriptLoaded.current = true;
            mount();
          };
          document.head.appendChild(script);

          if (cssFile) {
            const css = document.createElement("link");
            css.rel = "stylesheet";
            css.crossOrigin = "cors";
            css.href = `${host}${cssFile}`;
            document.head.appendChild(css);
          }
        })
        .catch(() => {});
    };

    slotPoll = setInterval(() => {
      if (!drawerSlotRef.current) return;
      clearInterval(slotPoll);

      loadScript();
    }, 50);

    return () => {
      clearInterval(slotPoll);
      clearInterval(scriptPoll);
    };
  }, [sliderOpen]);

  useEffect(() => {
    if (!sliderOpen || !drawerMounted.current) return;

    const el = drawerSlotRef.current;
    if (!el || !window.renderDrawerComponent) return;

    // renderDrawerComponent uses createRoot internally; unmount first
    window.unmountDrawerComponent?.();
    drawerMounted.current = false;

    window.renderDrawerComponent(el, {
      open: sliderOpen,
      expanded: drawerExpanded,
      compact: !drawerExpanded,
    });

    drawerMounted.current = true;
  }, [drawerExpanded, sliderOpen]);

  useEffect(() => {
    document.body.style.backgroundColor =
      theme === "light" ? "#FFFFFF" : "#101010";
  }, [theme]);

  const toggleMode = async () => {
    // Read the stored copy (localStorage) rather than `settings` from context:
    // grid column changes are written straight to storage, so the context
    // copy can be missing them and this save would overwrite them.
    const currentSettings = getRuntimeValue("settings") ?? settings;
    const platform = currentSettings?.platform || {};
    setMode((prev) => {
      let currVal = prev === "dark" ? "light" : "dark";
      sendDataToChild({ eventType: ENUM_EVENT.THEME, theme: currVal });
      return currVal;
    });
    const themeObj = {
      ...currentSettings,
      platform: {
        ...platform,
        theme: theme === "light" ? "dark" : "light",
      },
    };
    document.body.style.backgroundColor =
      theme === "light" ? "#FFFFFF" : "#101010";
    setSettings(themeObj);
    const payload = {
      childId: null,
      childType: null,
      product: null,
      settings: themeObj,
    };
    await updateSettings({
      payload,
      tenant_id: user?.tenant_id,
      bu_id: selectedBusinessUnit?.business_unit_id,
    });
  };

  const handleCloseClick = () => {
    setOpen(false);
  };

  //Logic for header
  const { selectedMenu } = useSelectedMenu();
  return (
    <ThemeContext.Provider value={{ toggleMode }}>
      <ThemeProvider theme={customTheme[theme]}>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          {/* Styled AppBar */}
          <TopNav
            selectedMenu={selectedMenu}
            onAiChatClick={handleAiChatClick}
          />
          {/* Side nav drawer */}

          <SideNav
            open={open}
            handleDrawerToggle={handleDrawerToggle}
            handleCloseClick={handleCloseClick}
            selectedMenu={selectedMenu}
          />
          {/* Main Content */}
          <Box
            component="main"
            sx={{
              width: "100%",
              flexGrow: 1,
              background: podTheme.palette.primary.parentBackground,
              marginTop: "4rem",
              marginLeft: "4rem", // Offset for closed drawer width
              overflowY: "auto", // Enable vertical scrolling for main content
              height: "calc(100vh - 4rem)", // Full height minus AppBar height
              transition: (theme) =>
                theme.transitions.create("margin", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            }}
          >
            {children}
          </Box>
        </Box>

        {/* Drawer for chatbot */}
        <SideDrawer
          onCloseClick={() => setSliderOpen(false)}
          isDrawerOpen={sliderOpen}
          expanded={drawerExpanded}
          setExpanded={setDrawerExpanded}
          drawerTitle={t("PlanPilot")}
        >
          <div
            ref={drawerSlotRef}
            style={{ height: "100%" }}
            onClick={(e) => e.stopPropagation()}
          />
        </SideDrawer>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default SCAILayout;